import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, Shield, Star, Trophy, ArrowRight, Check,
  Calculator, BookOpen, Languages, Globe, FlaskConical, Rocket,
  Palmtree, Sun, Waves, GraduationCap, Zap, Circle, Smile, Gift, Trees, HeartPulse,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import useLiveGrades from "@/lib/useLiveGrades";
import { useState } from "react";
import { IntroVideoPlayer, LangToggle } from "@/components/intro/IntroVideoModal";
import FeatureShowcase from "@/components/intro/FeatureShowcase";

const SUBJECTS = [
  { name: "Maths", Icon: Calculator, color: "#FF6B4A", grades: "Grades 1–6" },
  { name: "English", Icon: BookOpen, color: "#3D8BFF", grades: "Grades 1–6" },
  { name: "French", Icon: Languages, color: "#FFB020", grades: "Grades 1–6" },
  { name: "Health & PE", Icon: HeartPulse, color: "#EC4899", grades: "Grades 1–3" },
  { name: "SSEE", Icon: Trees, color: "#14B8A6", grades: "Grade 3" },
  { name: "History & Geo", Icon: Globe, color: "#22C08A", grades: "Grades 4–6" },
  { name: "Science", Icon: FlaskConical, color: "#A66CFF", grades: "Grades 4–6" },
];

const FEATURES = [
  { Icon: Star, title: "Earn Stars", text: "Kids collect stars on every level they master.", color: "#FFB020" },
  { Icon: Shield, title: "Win Shields", text: "Complete a whole chapter to unlock a shield.", color: "#3D8BFF" },
  { Icon: Trophy, title: "Level Up", text: "3 levels per chapter — Easy, Medium, Difficult.", color: "#FFB020" },
  { Icon: Sparkles, title: "Praise & Rewards", text: "Cheerful praise keeps young learners motivated.", color: "#22C08A" },
  { Icon: Smile, title: "Collect Avatars", text: "Master a chapter to earn tokens and unlock fun new avatars.", color: "#A66CFF" },
  { Icon: Gift, title: "Special Gift", text: "12 months of continuous subscription (Holiday Pack = 2 months) and an 80% achievement rate = a special gift!", color: "#FF6B4A" },
];

export default function Landing() {
  const liveGrades = useLiveGrades();
  const { user } = useAuth();
  const [vLang, setVLang] = useState("fr");
  return (
    <div className="min-h-screen bg-white font-parent text-slate-900 overflow-x-hidden">
      {/* Nav */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/90 border-b border-slate-900/5">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            <div className="w-9 h-9 rounded-xl bg-blue-600 border-2 border-slate-900 flex items-center justify-center shadow-[3px_3px_0_0_#0F172A]">
              <Rocket color="#fff" size={20} />
            </div>
            <span className="font-kids text-xl font-bold whitespace-nowrap hidden sm:inline">Anou Appran</span>
          </Link>
          <nav className="flex items-center gap-2 sm:gap-3">
            <Link to="/pricing" className="text-sm font-medium hover:underline px-3" data-testid="nav-pricing">Pricing</Link>
            {user?.is_admin && (
              <Link to="/admin" className="text-sm font-semibold hover:underline px-2" data-testid="nav-admin">Admin</Link>
            )}
            {user ? (
              <Link to="/parent"><Button className="rounded-full bg-[#3D8BFF] hover:bg-[#2B74E8] text-white" data-testid="nav-dashboard">Dashboard</Button></Link>
            ) : (
              <>
                <Link to="/login" className="text-sm font-medium hover:underline px-2 whitespace-nowrap" data-testid="nav-login">Log in</Link>
                <Link to="/register"><Button className="rounded-full bg-[#3D8BFF] hover:bg-[#2B74E8] text-white" data-testid="nav-register">Get started</Button></Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <div className="relative bg-white pt-6 pb-4 px-3 sm:px-5" data-testid="hero-band">
      <div className="relative max-w-6xl mx-auto rounded-[40px] bg-[#1E5EFF] text-white overflow-hidden shadow-[0_30px_70px_rgba(30,94,255,0.35)]">
      <div aria-hidden className="absolute -right-24 -bottom-32 w-[420px] h-[420px] rounded-full bg-[#3D7BFF]" />
      <div aria-hidden className="absolute -left-16 -top-20 w-56 h-56 rounded-full bg-[#1449D6]" />
      <Star aria-hidden className="absolute top-12 right-[10%] text-[#FFC83D] float-slow" fill="#FFC83D" size={30} strokeWidth={2.5} />
      <Circle aria-hidden className="absolute bottom-8 left-[40%] text-[#FF6B4A] float-slow [animation-delay:0.6s]" fill="#FF6B4A" size={18} strokeWidth={2.5} />
      <Zap aria-hidden className="absolute bottom-12 left-[47%] text-white float-slow [animation-delay:1.2s]" fill="#FFFFFF" size={26} strokeWidth={2.5} />
      <section className="relative px-6 sm:px-12 pt-12 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.15em] uppercase bg-[#FFC83D] text-[#1F2A44] rounded-full px-3 py-1.5">
            <Palmtree size={14} /> Anou aprann ansam! · Grades 1–6
          </span>
          <h1 className="font-kids text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-5 leading-[1.05] text-white">
            Learning that feels like{" "}
            <span className="text-[#FFC83D]">playtime</span>.
          </h1>
          <p className="text-base sm:text-lg text-blue-50 mt-6 max-w-md leading-relaxed">
            Fun daily revision for every primary grade, from Grade 1 to Grade 6, made for young Mauritian learners. Questions and answers are read aloud for the youngest. Kids earn stars, shields, avatars and praise, plus a special gift after 12 months of continuous subscription and an achievement rate of 80%, while parents watch every step of progress.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to={user ? "/parent" : "/register"}>
              <Button size="lg" className="rounded-full h-12 px-7 text-base bg-[#FFC83D] text-[#1F2A44] hover:bg-[#FFB800] shadow-[0_10px_24px_rgba(0,0,0,0.2)] hover:shadow-[2px_2px_0_0_#0F172A] hover:translate-x-0.5 hover:translate-y-0.5 transition-all" data-testid="hero-cta">
                Start 10-min free trial <ArrowRight size={18} className="ml-1" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="rounded-full h-12 px-7 text-base border-0 bg-white text-[#1F2A44] hover:bg-blue-50 shadow-[0_6px_18px_rgba(31,42,68,0.08)]" data-testid="hero-pricing">
                See plans
              </Button>
            </Link>
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-7 text-sm text-white font-semibold">
            <span className="flex items-center gap-1.5 bg-white/15 rounded-xl px-3 py-2"><span className="w-5 h-5 rounded-full bg-[#FFC83D] flex items-center justify-center"><Check size={12} className="text-[#1F2A44]" strokeWidth={3} /></span> 10 min free, full access</span>
            <span className="flex items-center gap-1.5 bg-white/15 rounded-xl px-3 py-2"><span className="w-5 h-5 rounded-full bg-[#FFC83D] flex items-center justify-center"><Check size={12} className="text-[#1F2A44]" strokeWidth={3} /></span> Multiple kids</span>
            <span className="flex items-center gap-1.5 bg-white/15 rounded-xl px-3 py-2"><span className="w-5 h-5 rounded-full bg-[#FFC83D] flex items-center justify-center"><Check size={12} className="text-[#1F2A44]" strokeWidth={3} /></span> Parent dashboard</span>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.1 }} className="relative h-[380px] sm:h-[420px]">
          <motion.img src="/intro/shots/parent.webp" alt="Parent dashboard" data-testid="hero-illustration"
            initial={{ rotate: 0 }} animate={{ rotate: -4 }} transition={{ delay: 0.3, type: "spring" }}
            className="absolute left-0 top-0 w-[82%] rounded-[1.6rem] border-4 border-[#111] shadow-[8px_8px_0_0_#111] bg-white" />
          <motion.img src="/intro/shots/reading.webp" alt="Reading with Kiko"
            initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0, rotate: 3 }} transition={{ delay: 0.45, type: "spring" }}
            className="absolute right-0 top-[38%] w-[70%] rounded-[1.6rem] border-4 border-[#111] shadow-[8px_8px_0_0_#111] bg-white" />
          <motion.img src="/mascot/kiko.png" alt="Kiko" animate={{ y: [0, -10, 0] }} transition={{ repeat: Infinity, duration: 3 }}
            className="absolute -left-6 bottom-0 w-28 sm:w-32 drop-shadow-[4px_4px_0_#111]" />
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
            className="absolute -top-4 right-2 bg-white text-slate-900 rounded-2xl border-[3px] border-[#111] shadow-[4px_4px_0_0_#111] px-4 py-3 flex items-center gap-3"
          >
            <div className="w-11 h-11 rounded-full bg-[#FFC83D] border-2 border-[#111] flex items-center justify-center">
              <Star fill="#fff" color="#111" size={20} />
            </div>
            <div>
              <p className="font-kids font-bold leading-none">+3 Stars!</p>
              <p className="text-xs text-slate-500 mt-0.5">Level complete</p>
            </div>
          </motion.div>
        </motion.div>
      </section>
      </div>
      </div>

      {/* Video tour */}
      <section className="max-w-6xl mx-auto px-5 pt-16" data-testid="landing-video-section">
        <div className="grid lg:grid-cols-[1fr_1.5fr] gap-10 items-start">
          <div>
            <span className="inline-block rounded-full border-2 border-[#111] bg-[#B7F000] px-3 py-1 text-xs font-black uppercase tracking-[0.18em]">Video tour · 2 min</span>
            <h2 className="font-kids text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mt-4 leading-tight">See everything your child will do</h2>
            <p className="text-slate-600 mt-4 text-lg">Sign up, subjects, exercises read aloud, rewards, the Reading kit with Kiko, your progress reports and payment, all in one short video. En français ou en anglais.</p>
            <div className="mt-6"><LangToggle lang={vLang} setLang={setVLang} /></div>
          </div>
          <IntroVideoPlayer lang={vLang} mandatory={false} />
        </div>
      </section>

      {/* Feature showcase with real screens */}
      <section className="max-w-6xl mx-auto px-5 pt-20" data-testid="landing-showcase">
        <h2 className="font-kids text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-10 max-w-3xl">One app for every subject, reading and rewards</h2>
        <FeatureShowcase lang="en" />
      </section>

      {/* Subjects */}
      <section className="max-w-6xl mx-auto px-5 pt-12 pb-16">
        <p className="flex items-center gap-2 text-sm tracking-[0.15em] uppercase text-slate-500 font-semibold mb-5">
          <Sun size={16} className="text-amber-500" /> 7 subjects across Grades 1–6 <Waves size={16} className="text-teal-500" />
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
          {SUBJECTS.map((s, i) => (
            <motion.div
              key={s.name}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="rounded-3xl p-5 flex flex-col items-center gap-3 cursor-default relative text-white shadow-[0_12px_28px_rgba(31,42,68,0.15)] hover:-translate-y-1 transition-transform duration-200"
              style={{ background: s.color }}
              data-testid={`landing-subject-${s.name}`}
            >
              <div className="w-16 h-16 rounded-2xl bg-white/25 flex items-center justify-center">
                <s.Icon color="#fff" size={30} />
              </div>
              <span className="font-kids font-bold text-lg text-center">{s.name}</span>
              <span className="text-[10px] font-semibold tracking-wide uppercase text-[#1F2A44] bg-white/90 rounded-full px-2 py-0.5">
                {s.grades}
              </span>
            </motion.div>
          ))}
        </div>

        {/* Grades */}
        <p className="flex items-center gap-2 text-sm tracking-[0.15em] uppercase text-slate-500 font-semibold mt-14 mb-5">
          <GraduationCap size={16} className="text-blue-500" /> 6 grades to grow with
        </p>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-4">
          {[1, 2, 3, 4, 5, 6].map((g, i) => {
            const colors = ["#3D8BFF", "#FF6B4A", "#A66CFF", "#FFB020", "#22C08A", "#3D8BFF"];
            const soon = !liveGrades.includes(g);
            return (
              <motion.div
                key={g}
                initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
                transition={{ delay: i * 0.05, type: "spring", stiffness: 300, damping: 18 }}
                className={`rounded-3xl shadow-[0_12px_28px_rgba(31,42,68,0.08)] ${soon ? "opacity-60" : "hover:-translate-y-1 transition-transform duration-200"} bg-white p-5 flex flex-col items-center gap-2 cursor-default`}
                data-testid={`grade-tile-${g}`}
              >
                <div className="w-12 h-12 rounded-full flex items-center justify-center font-kids text-xl font-bold text-white" style={{ background: soon ? "#94A3B8" : colors[i] }}>
                  {g}
                </div>
                <span className="font-kids font-semibold text-center text-sm">Grade {g}</span>
                {soon && (
                  <span className="text-[9px] font-semibold tracking-wide uppercase text-slate-500">Coming soon</span>
                )}
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Gamification features */}
      <section className="max-w-6xl mx-auto px-5 py-16">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="font-kids text-2xl sm:text-3xl lg:text-4xl font-bold">An encouraging, stimulating platform for kids to progress</h2>
          <p className="text-slate-600 mt-3">Every correct answer is a little celebration. Progress unlocks step by step, so learning always feels achievable.</p>
        </div>
        <div className="flex justify-center -mt-4 mb-8">
          <Link to="/rewards" className="inline-flex items-center gap-1 font-semibold text-[#3D8BFF] hover:underline" data-testid="landing-rewards-link">How to earn rewards <ArrowRight size={16} /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="rounded-3xl shadow-[0_12px_28px_rgba(31,42,68,0.08)] hover:-translate-y-1 transition-transform duration-200 bg-white p-6"
            >
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: f.color }}>
                <f.Icon color="#fff" size={22} />
              </div>
              <h3 className="font-kids font-bold text-lg">{f.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-5 pb-24">
        <div className="rounded-[40px] bg-[#3D8BFF] text-white p-10 sm:p-14 text-center shadow-[0_24px_60px_rgba(47,128,237,0.3)]">
          <h2 className="font-kids text-3xl sm:text-4xl font-bold">Ready to make revision fun?</h2>
          <p className="mt-3 text-blue-100 max-w-lg mx-auto">Create a free account, add your child and enjoy 10 minutes of full access to every subject and the Reading kit.</p>
          <Link to={user ? "/parent" : "/register"}>
            <Button size="lg" className="mt-7 rounded-full h-12 px-8 bg-white text-[#3D8BFF] hover:bg-blue-50 font-semibold shadow-[0_10px_24px_rgba(0,0,0,0.15)]" data-testid="cta-bottom">
              Get started free <ArrowRight size={18} className="ml-1" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-slate-900/10 py-8 text-center text-sm text-slate-500">
        Anou Appran — aprann ansam, dan enn fason amizan. 🌴
      </footer>
    </div>
  );
}
